import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useToast } from 'components/ui/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import { Question } from '../../types';
import { fetchStreamingAiSummary } from '../../services/api';
import { Button } from '../ui/button';
import { TextLinesShimmer } from '../ui/text-lines-shimmer';

interface AiSummaryQuestionProps {
  question: Question;
  textValue: string;
  onTextChange: (value: string) => void;
  onLoadingStateChange?: (isLoading: boolean) => void;
  isSubmitting?: boolean;
  flowRunId?: number | null;
}

export const AiSummaryQuestion: React.FC<AiSummaryQuestionProps> = ({
  question,
  textValue,
  onTextChange,
  onLoadingStateChange,
  isSubmitting = false,
  flowRunId,
}) => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const isAdmin = Boolean(user?.is_staff);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [streamedText, setStreamedText] = useState('');
  const streamedTextRef = useRef('');
  const lastGeneratedQuestionRef = useRef<number | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const generateSummary = useCallback(
    (questionId: number) => {
      abortControllerRef.current?.abort();

      streamedTextRef.current = '';
      setStreamedText('');
      setGenerationError(null);
      setIsLoadingSummary(true);

      const controller = fetchStreamingAiSummary(
        questionId,
        flowRunId ?? undefined,
        (chunk) => {
          streamedTextRef.current += chunk;
          setStreamedText(streamedTextRef.current);
        },
        () => {
          onTextChange(streamedTextRef.current);
          setIsLoadingSummary(false);
          lastGeneratedQuestionRef.current = questionId;
        },
        (err) => {
          setGenerationError("We couldn't generate your summary. Please try again.");
          addToast({
            variant: 'destructive',
            title: 'Summary generation failed',
            description: 'Your place is saved. You can retry without leaving this question.',
          });
          setIsLoadingSummary(false);
          void err;
        },
      );

      abortControllerRef.current = controller;
    },
    [addToast, flowRunId, onTextChange],
  );

  // Auto-generate on mount when there's no existing summary.
  useEffect(() => {
    const rawQuestionId = question?.id;

    if (typeof rawQuestionId !== 'number') {
      lastGeneratedQuestionRef.current = null;
      return;
    }

    const questionId = rawQuestionId;

    // If we already have text, don't regenerate
    if (textValue?.trim()) {
      lastGeneratedQuestionRef.current = questionId;
      return;
    }

    // Don't regenerate for the same question
    if (lastGeneratedQuestionRef.current === questionId) {
      return;
    }

    lastGeneratedQuestionRef.current = questionId;
    generateSummary(questionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.id, generateSummary]);

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      // React StrictMode intentionally mounts/unmounts once in development.
      // Reset this guard so the real mount can restart generation.
      lastGeneratedQuestionRef.current = null;
      abortControllerRef.current?.abort();
    };
    // Intentionally unmount-only cleanup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRegenerate = useCallback(() => {
    if (isLoadingSummary) {
      return;
    }

    const rawQuestionId = question?.id;
    if (typeof rawQuestionId !== 'number') {
      addToast({ variant: 'destructive', title: 'Unable to generate summary for this question' });
      return;
    }

    generateSummary(rawQuestionId);
  }, [addToast, isLoadingSummary, question.id, generateSummary]);

  // Show the streamed text while loading, or the final textValue once done
  const displayText = isLoadingSummary ? streamedText : textValue;

  useEffect(() => {
    onLoadingStateChange?.(isLoadingSummary);
  }, [isLoadingSummary, onLoadingStateChange]);

  if (isLoadingSummary && !streamedText) {
    return (
      <div className="w-full h-full">
        <TextLinesShimmer lineCount={6} className="w-full h-full" />
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      <div className="whitespace-pre-wrap text-foreground font-body-m leading-relaxed min-h-[80px]">
        {displayText || 'No summary available.'}
      </div>
      {generationError ? (
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3"
        >
          <p className="text-sm text-destructive">{generationError}</p>
          <Button
            type="button"
            variant="secondary"
            size="auto"
            onClick={handleRegenerate}
            disabled={isSubmitting || isLoadingSummary}
          >
            Retry Summary
          </Button>
        </div>
      ) : null}
      {isAdmin && (
        <div className="flex justify-end">
          <Button
            size="auto"
            onClick={handleRegenerate}
            disabled={isSubmitting || isLoadingSummary}
          >
            Regenerate Summary
          </Button>
        </div>
      )}
    </div>
  );
};
